import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  LayoutDashboard, Users, Film, Coins, Activity, FlaskConical, LogOut, Loader2,
  Wallet, CreditCard, Building2, Check, X, ExternalLink, MapPin, MessageCircle,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  getAdminOverview, getAdminUsers, getAdminSalesVideos, getAdminCreditTransactions,
  getAdminHealth,
  getAdminPaymentSettings, updateAdminPaymentSettings, getAdminPackages, updateAdminPackage,
  getAdminPaymentOrders, reconcileAdminPaymentOrder, approvePaymentOrder, rejectPaymentOrder, openPaymentProof,
  getAdminVideoModels, setAdminVideoModelEnabled, setAdminDefaultVideoModel, reconcileAdminSalesVideo,
} from "@/lib/api";
import { BUSINESS, formatRupiah, waLink } from "@/lib/site";
import { needsReview, statusLabel } from "@/lib/salesVideoStatus";
import { paymentStatusLabel } from "@/lib/paymentStatus";
import AdminSalesVideoTemplates from "@/components/AdminSalesVideoTemplates";
import ShortDramaHistory from "@/components/ShortDramaHistory";

const TABS = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "users", label: "Users", icon: Users },
  { id: "jobs", label: "Video Jobs", icon: Film },
  { id: "models", label: "Video Models", icon: FlaskConical },
  { id: "templates", label: "Template Video", icon: Film },
  { id: "shortdramas", label: "ShortDrama Jobs", icon: Film },
  { id: "credits", label: "Credit Transactions", icon: Coins },
  { id: "payments", label: "Payment Management", icon: CreditCard },
  { id: "settings", label: "Payment Settings", icon: Wallet },
  { id: "business", label: "Business Info", icon: Building2 },
  { id: "health", label: "System Health", icon: Activity },
];

const Stat = ({ label, value, tid }) => (
  <div className="rounded-xl border border-slate-800 bg-slate-900 p-4" data-testid={tid}>
    <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
    <p className="mt-1 text-2xl font-bold text-white">{value}</p>
  </div>
);

const MethodToggle = ({ name, note, enabled, onToggle, testid }) => (
  <div className="flex items-center justify-between rounded-lg border border-slate-800 bg-slate-900 px-4 py-3">
    <div>
      <p className="font-medium text-white">{name}</p>
      {note && <p className="text-xs text-slate-500">{note}</p>}
    </div>
    <button onClick={() => onToggle(!enabled)} data-testid={testid}
      className={`relative h-6 w-11 rounded-full transition-colors ${enabled ? "bg-emerald-600" : "bg-slate-600"}`}>
      <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${enabled ? "translate-x-5" : "translate-x-0.5"}`} />
    </button>
  </div>
);

const HealthRow = ({ name, data }) => {
  const color = { READY: "text-emerald-400", CONFIGURED_UNVERIFIED: "text-sky-400", NOT_CONFIGURED: "text-amber-400",
    DISABLED: "text-slate-400", ERROR: "text-red-400" }[data.status] || "text-slate-400";
  return (
    <div className="flex items-center justify-between rounded-lg border border-slate-800 bg-slate-900 px-4 py-3">
      <div>
        <p className="font-medium text-white">{name}</p>
        {data.model && <p className="text-xs text-slate-500">Model: {data.model}</p>}
        {data.status === "CONFIGURED_UNVERIFIED" && <p className="text-xs text-slate-500" data-testid={`health-note-${name.toLowerCase()}`}>Kredensial ada; belum diverifikasi ke penyedia.</p>}
      </div>
      <span className={`text-sm font-semibold ${color}`} data-testid={`health-${name.toLowerCase()}`}>{data.status}</span>
    </div>
  );
};

export default function AdminDashboard() {
  const [tab, setTab] = useState("overview");
  const [ov, setOv] = useState(null);
  const [users, setUsers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [txns, setTxns] = useState([]);
  const [health, setHealth] = useState(null);
  const [paySettings, setPaySettings] = useState(null);
  const [packages, setPackages] = useState([]);
  const [priceEdits, setPriceEdits] = useState({});
  const [orders, setOrders] = useState([]);
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [acting, setActing] = useState(null);
  const navigate = useNavigate();
  const { logout: endSession } = useAuth();
  const enabledModelCount = models.filter((model) => model.enabled).length;

  const loadPayments = async () => {
    setOrders((await getAdminPaymentOrders()).orders);
  };
  const loadSettings = async () => {
    setPaySettings(await getAdminPaymentSettings());
    setPackages((await getAdminPackages()).packages);
  };
  const loadModels = async () => setModels((await getAdminVideoModels()).models || []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        if (tab === "overview") setOv(await getAdminOverview());
        else if (tab === "users") setUsers((await getAdminUsers()).users);
        else if (tab === "jobs") setJobs((await getAdminSalesVideos()).jobs);
        else if (tab === "models") await loadModels();
        else if (tab === "credits") setTxns((await getAdminCreditTransactions()).transactions);
        else if (tab === "payments") await loadPayments();
        else if (tab === "settings") await loadSettings();
        else if (tab === "health") setHealth(await getAdminHealth());
      } catch (error) { toast.error(error?.response?.data?.detail || "Gagal memuat data Admin."); } finally { setLoading(false); }
    };
    load();
  }, [tab]);

  const toggleMethod = async (key, value) => {
    try {
      const updated = await updateAdminPaymentSettings({ [key]: value });
      setPaySettings(updated);
      toast.success("Pengaturan pembayaran disimpan.");
    } catch { toast.error("Gagal menyimpan pengaturan."); }
  };

  const togglePackage = async (code, is_active) => {
    try {
      await updateAdminPackage(code, { is_active });
      setPackages((await getAdminPackages()).packages);
      toast.success("Paket diperbarui.");
    } catch { toast.error("Gagal memperbarui paket."); }
  };

  const approve = async (order) => {
    // Approval adds credit at once and the dashboard has no way to take it back: show exactly what will happen first.
    const summary = [
      `Setujui pembayaran ${order.user_email || "pengguna ini"}?`,
      `${order.package_name || order.package_code || "Paket"}: ${formatRupiah(order.amount)} → +${order.credits} kredit.`,
      order.proof_url ? "Bukti pembayaran terlampir." : "BELUM ada bukti pembayaran.",
      "Kredit langsung ditambahkan dan tidak dapat ditarik kembali dari sini.",
    ].join("\n");
    if (!window.confirm(summary)) return;
    const id = order.id;
    setActing(id);
    try {
      const res = await approvePaymentOrder(id);
      toast.success(res.credited ? `Disetujui. +${res.credits} kredit ditambahkan.` : "Pesanan sudah lunas.");
      await loadPayments();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyetujui."); }
    finally { setActing(null); }
  };

  const reject = async (id) => {
    const reason = window.prompt("Alasan penolakan (opsional):", "");
    if (reason === null) return;
    setActing(id);
    try {
      await rejectPaymentOrder(id, reason);
      toast.success("Pesanan ditolak.");
      await loadPayments();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menolak."); }
    finally { setActing(null); }
  };

  const reconcilePayment = async (id) => {
    setActing(id);
    try {
      await reconcileAdminPaymentOrder(id);
      toast.success("Status pembayaran diperiksa.");
      await loadPayments();
    } catch (e) { toast.error(e?.response?.data?.detail || "Status pembayaran belum dapat dipastikan."); }
    finally { setActing(null); }
  };

  const logout = async () => { await endSession(); navigate("/admin/masuk", { replace: true }); };
  const savePackagePrice = async (pkg) => {
    const raw = String(priceEdits[pkg.code] ?? pkg.price);
    const price = Number(raw);
    if (!/^\d+$/.test(raw) || !Number.isSafeInteger(price) || price < 1 || price > 100000000) {
      toast.error("Harga harus bilangan bulat antara Rp1 dan Rp100.000.000.");
      return;
    }
    setActing(`price:${pkg.code}`);
    try {
      await updateAdminPackage(pkg.code, { price });
      setPackages((await getAdminPackages()).packages || []);
      setPriceEdits((current) => { const next = { ...current }; delete next[pkg.code]; return next; });
      toast.success("Harga paket disimpan.");
    } catch (error) { toast.error(error?.response?.data?.detail || "Gagal menyimpan harga."); }
    finally { setActing(null); }
  };
  const changeModel = async (model, makeDefault = false) => {
    setActing(model.model_key);
    try {
      if (makeDefault) await setAdminDefaultVideoModel(model.model_key);
      else await setAdminVideoModelEnabled(model.model_key, !model.enabled);
      await loadModels();
      toast.success("Pengaturan model disimpan.");
    } catch (error) { toast.error(error?.response?.data?.detail || "Gagal memperbarui model."); }
    finally { setActing(null); }
  };
  const reconcile = async (id) => {
    setActing(id);
    try {
      await reconcileAdminSalesVideo(id);
      setJobs((await getAdminSalesVideos()).jobs || []);
      toast.success("Status video diperiksa.");
    } catch (error) { toast.error(error?.response?.data?.detail || "Status belum dapat dipastikan."); }
    finally { setActing(null); }
  };
  const viewProof = async (id) => {
    try { await openPaymentProof(id); }
    catch (error) { toast.error(error?.message || "Gagal membuka bukti pembayaran."); }
  };

  return (
    <div className="flex min-h-screen bg-slate-950 text-slate-200" data-testid="admin-dashboard">
      <aside className="w-60 shrink-0 border-r border-slate-800 bg-slate-900 p-4">
        <p className="mb-6 px-2 font-heading text-lg font-bold text-white">Mas Tofa Admin</p>
        <nav className="space-y-1">
          {TABS.map((t) => (
            <button key={t.id} data-testid={`admin-tab-${t.id}`} onClick={() => setTab(t.id)}
              className={`flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm ${tab === t.id ? "bg-indigo-600 text-white" : "text-slate-400 hover:bg-slate-800"}`}>
              <t.icon className="h-4 w-4" /> {t.label}
            </button>
          ))}
          <Link to="/admin/test-video" data-testid="admin-tab-test-video"
            className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-emerald-400 hover:bg-slate-800">
            <FlaskConical className="h-4 w-4" /> Test Video
          </Link>
          <Link to="/admin/affiliate" className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-slate-400 hover:bg-slate-800">
            <Users className="h-4 w-4" /> Affiliate
          </Link>
          <Link to="/admin/business-settings" className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-slate-400 hover:bg-slate-800">
            <Activity className="h-4 w-4" /> Tracking & WhatsApp
          </Link>
          <Link to="/admin/model-control" data-testid="admin-tab-model-control" className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-slate-400 hover:bg-slate-800">
            <Activity className="h-4 w-4" /> Kontrol Model
          </Link>
          <Link to="/admin/keuangan" data-testid="admin-tab-finance" className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-slate-400 hover:bg-slate-800">
            <Activity className="h-4 w-4" /> Keuangan
          </Link>
          <Link to="/admin/refund" data-testid="admin-tab-refunds" className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-slate-400 hover:bg-slate-800">
            <Activity className="h-4 w-4" /> Tinjauan Refund
          </Link>
        </nav>
        <Button onClick={logout} variant="ghost" data-testid="admin-logout-btn"
          className="mt-8 w-full justify-start text-slate-400 hover:bg-slate-800 hover:text-white">
          <LogOut className="mr-2 h-4 w-4" /> Keluar
        </Button>
      </aside>

      <main className="flex-1 overflow-auto p-8">
        {loading && <div className="flex items-center gap-2 text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Memuat…</div>}

        {tab === "overview" && ov && (
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4" data-testid="admin-overview">
            <Stat label="Total User" value={ov.users_total} tid="stat-users" />
            <Stat label="Member" value={ov.members} tid="stat-members" />
            <Stat label="Total Video" value={ov.videos_total} tid="stat-videos" />
            <Stat label="Completed" value={ov.videos_completed} tid="stat-completed" />
            <Stat label="Processing" value={ov.videos_processing} tid="stat-processing" />
            <Stat label="Gagal" value={ov.videos_failed} tid="stat-failed" />
            <Stat label="Perlu Review" value={ov.videos_needs_review} tid="stat-review" />
            <Stat label="Total Credit Balance" value={ov.total_credit_balance} tid="stat-balance" />
            <Stat label="Credits Terpakai" value={ov.credits_used} tid="stat-used" />
            <Stat label="Credits Ditahan" value={ov.credits_held} tid="stat-held" />
          </div>
        )}

        {tab === "users" && (
          <table className="w-full text-sm" data-testid="admin-users-table">
            <thead><tr className="text-left text-slate-500">
              <th className="p-2">Nama</th><th className="p-2">Email</th><th className="p-2">Role</th><th className="p-2">Kredit</th><th className="p-2">Daftar</th>
            </tr></thead>
            <tbody>{users.map((u) => (
              <tr key={u.id} className="border-t border-slate-800">
                <td className="p-2">{u.name}</td><td className="p-2">{u.email}</td>
                <td className="p-2"><span className={u.role === "admin" ? "text-indigo-400" : "text-slate-400"}>{u.role}</span></td>
                <td className="p-2">{u.credit_balance}</td>
                <td className="p-2 text-slate-500">{(u.created_at || "").slice(0, 10)}</td>
              </tr>
            ))}</tbody>
          </table>
        )}

        {tab === "jobs" && (
          <table className="w-full text-sm" data-testid="admin-jobs-table">
            <thead><tr className="text-left text-slate-500">
              <th className="p-2">Judul / Model</th><th className="p-2">Status</th><th className="p-2">Provider Status</th><th className="p-2">Credit</th><th className="p-2">Dibuat</th><th className="p-2">Aksi</th>
            </tr></thead>
            <tbody>{jobs.map((j) => (
              <tr key={j.id} className="border-t border-slate-800">
                <td className="p-2 max-w-xs truncate">{j.title}<p className="text-xs text-slate-500">{j.model_display_name || j.model_key || ""}</p></td>
                <td className="p-2">{statusLabel(j)}</td>
                <td className="p-2 text-slate-400">{j.provider_status || "-"}</td>
                <td className="p-2">{j.credit_status || "-"}</td>
                <td className="p-2 text-slate-500">{(j.created_at || "").slice(0, 10)}</td>
                <td className="p-2">{(j.provider === "kie_grok" || j.provider === "kie") && (needsReview(j) || j.provider_status || j.credit_status) && (
                  <button onClick={() => reconcile(j.id)} disabled={acting === j.id} data-testid={`reconcile-video-${j.id}`}
                    className="rounded-md bg-indigo-600 px-3 py-1.5 text-xs text-white disabled:opacity-50">
                    {acting === j.id ? "Memeriksa…" : "Rekonsiliasi"}
                  </button>
                )}</td>
              </tr>
            ))}</tbody>
          </table>
        )}

        {tab === "models" && (
          <div className="max-w-3xl space-y-4" data-testid="admin-video-models">
            <h2 className="font-heading text-lg font-bold text-white">Model Video</h2>
            <p className="text-sm text-slate-400">ON/OFF berlaku untuk generasi baru. Video yang sedang berjalan memakai model dan harga yang sudah tersimpan.</p>
            {!models.length && !loading && <p className="text-sm text-slate-500">Model belum tersedia.</p>}
            {models.map((model) => (
              <div key={model.model_key} className="rounded-xl border border-slate-800 bg-slate-900 p-5" data-testid={`admin-model-${model.model_key}`}>
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <p className="font-semibold text-white">{model.display_name} {model.is_default && <span className="ml-2 text-xs text-indigo-300">Default</span>}</p>
                    <p className="mt-1 text-xs text-slate-500">{model.provider} · {model.provider_model_id}</p>
                    {model.description && <p className="mt-2 text-sm text-slate-400">{model.description}</p>}
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => changeModel(model)} disabled={acting === model.model_key || (model.enabled && enabledModelCount === 1)}
                      aria-label={`${model.enabled ? "Nonaktifkan" : "Aktifkan"} ${model.display_name}`}
                      data-testid={`toggle-model-${model.model_key}`}
                      className={`rounded-full px-4 py-2 text-sm font-semibold disabled:opacity-50 ${model.enabled ? "bg-emerald-600 text-white" : "bg-slate-700 text-slate-200"}`}>
                      {model.enabled ? "ON" : "OFF"}
                    </button>
                    <button onClick={() => changeModel(model, true)} disabled={!model.enabled || model.is_default || acting === model.model_key}
                      data-testid={`default-model-${model.model_key}`}
                      className="rounded-full border border-slate-700 px-4 py-2 text-sm disabled:opacity-40">Jadikan Default</button>
                  </div>
                </div>
                <div className="mt-4 space-y-1 text-xs text-slate-400">
                  <p>Kemampuan: {[model.capabilities?.text_to_video && "Teks ke video", model.capabilities?.image_to_video && "Gambar ke video"].filter(Boolean).join(" · ")}</p>
                  <p>Durasi: {model.capabilities?.duration_min}–{model.capabilities?.duration_max} detik · Resolusi: {(model.capabilities?.resolutions || []).join(", ")}</p>
                  <p>Rasio: {(model.capabilities?.aspect_ratios || []).join(", ")}</p>
                  <p>Format aplikasi: {model.credit_info?.application_duration_seconds || 6} detik · {(model.credit_info?.priced_resolutions || ["720p"]).join(", ")} · {model.credit_info?.credits_per_second} kredit/detik</p>
                  {model.enabled && enabledModelCount === 1 && <p className="text-amber-400">Setidaknya satu model harus tetap aktif.</p>}
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "templates" && <AdminSalesVideoTemplates />}
        {tab === "shortdramas" && <ShortDramaHistory admin />}

        {tab === "credits" && (
          <table className="w-full text-sm" data-testid="admin-credits-table">
            <thead><tr className="text-left text-slate-500">
              <th className="p-2">User</th><th className="p-2">Type</th><th className="p-2">Amount</th><th className="p-2">Status</th><th className="p-2">Waktu</th>
            </tr></thead>
            <tbody>{txns.map((t) => (
              <tr key={t.id} className="border-t border-slate-800">
                <td className="p-2 text-slate-400">{(t.user_id || "").slice(-6)}</td>
                <td className="p-2">{t.type}</td><td className="p-2">{t.amount}</td>
                <td className="p-2">{t.status}</td>
                <td className="p-2 text-slate-500">{(t.created_at || "").slice(0, 16)}</td>
              </tr>
            ))}</tbody>
          </table>
        )}

        {tab === "payments" && (
          <div data-testid="admin-payments">
            <h2 className="mb-4 font-heading text-lg font-bold text-white">Payment Management</h2>
            {orders.length === 0 ? (
              <p className="text-slate-500" data-testid="admin-payments-empty">Belum ada pesanan pembayaran.</p>
            ) : (
              <table className="w-full text-sm" data-testid="admin-payments-table">
                <thead><tr className="text-left text-slate-500">
                  <th className="p-2">User</th><th className="p-2">Paket</th><th className="p-2">Credit</th>
                  <th className="p-2">Jumlah</th><th className="p-2">Metode</th><th className="p-2">Status</th>
                  <th className="p-2">Bukti</th><th className="p-2">Tanggal</th><th className="p-2">Aksi</th>
                </tr></thead>
                <tbody>{orders.map((o) => (
                  <tr key={o.id} className="border-t border-slate-800" data-testid={`admin-order-${o.id}`}>
                    <td className="p-2 text-slate-300">{o.user_email || (o.user_id || "").slice(-6)}</td>
                    <td className="p-2">{o.package_name}</td>
                    <td className="p-2">{o.credits}</td>
                    <td className="p-2">{formatRupiah(o.amount)}</td>
                    <td className="p-2 text-slate-400">{o.method === "bca_manual" ? "BCA" : o.method === "tripay_qris" ? "QRIS / ShopeePay" : o.method}</td>
                    <td className="p-2" data-testid={`admin-order-status-${o.id}`}>{paymentStatusLabel(o.status)}<p className="text-xs text-slate-500">{o.status}</p></td>
                    <td className="p-2">
                      {(o.has_proof || o.proof_url) ? (
                        <button onClick={() => viewProof(o.id)}
                          className="inline-flex items-center gap-1 text-indigo-400 hover:underline" data-testid={`admin-proof-${o.id}`}>
                          Lihat <ExternalLink className="h-3 w-3" />
                        </button>
                      ) : <span className="text-slate-600">-</span>}
                    </td>
                    <td className="p-2 text-slate-500">{(o.created_at || "").slice(0, 16)}</td>
                    <td className="p-2">
                      {(o.status === "CREDITING" || (o.method === "tripay_qris" && ["SUBMITTING", "RECONCILING", "PENDING", "FAILED", "EXPIRED", "CANCELLED"].includes(o.status))) ? (
                        <button onClick={() => reconcilePayment(o.id)} disabled={acting === o.id} data-testid={`reconcile-payment-${o.id}`}
                          className="rounded-md bg-indigo-600 px-2 py-1 text-xs font-medium text-white hover:bg-indigo-700 disabled:opacity-50">
                          {acting === o.id ? <Loader2 className="inline h-3 w-3 animate-spin" /> : "Periksa"}
                        </button>
                      ) : o.method === "bca_manual" && (o.status === "PENDING" || o.status === "WAITING_VERIFICATION") ? (
                        <div className="flex gap-2">
                          <button onClick={() => approve(o)} disabled={acting === o.id} data-testid={`approve-${o.id}`}
                            className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-2 py-1 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-50">
                            {acting === o.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />} Approve
                          </button>
                          <button onClick={() => reject(o.id)} disabled={acting === o.id} data-testid={`reject-${o.id}`}
                            className="inline-flex items-center gap-1 rounded-md bg-red-600 px-2 py-1 text-xs font-medium text-white hover:bg-red-700 disabled:opacity-50">
                            <X className="h-3 w-3" /> Reject
                          </button>
                        </div>
                      ) : <span className="text-xs text-slate-600">{o.status === "PAID" ? "Selesai" : "-"}</span>}
                    </td>
                  </tr>
                ))}</tbody>
              </table>
            )}
          </div>
        )}

        {tab === "settings" && (
          <div className="max-w-2xl space-y-8" data-testid="admin-payment-settings">
            <div>
              <h2 className="mb-1 font-heading text-lg font-bold text-white">Metode Pembayaran</h2>
              <p className="mb-4 text-sm text-slate-500">Hanya metode aktif yang tampil ke member.</p>
              <div className="space-y-3">
                <MethodToggle name="QRIS / ShopeePay" note={paySettings?.qris_configuration?.configured
                  ? `TriPay siap · mode ${paySettings.qris_configuration.mode || "production"}`
                  : "TriPay belum dikonfigurasi — pembayaran QRIS belum tersedia"} enabled={!!paySettings?.qris}
                  onToggle={(v) => toggleMethod("qris", v)} testid="toggle-qris" />
                <MethodToggle name="Transfer Manual BCA" note="Verifikasi manual oleh Admin" enabled={!!paySettings?.bca}
                  onToggle={(v) => toggleMethod("bca", v)} testid="toggle-bca" />
              </div>
            </div>
            <div>
              <h2 className="mb-1 font-heading text-lg font-bold text-white">Paket Kredit</h2>
              <p className="mb-4 text-sm text-slate-500">Atur ketersediaan dan harga paket untuk pesanan berikutnya.</p>
              <div className="space-y-2">
                {packages.map((p) => (
                  <div key={p.code} className="flex items-center justify-between rounded-lg border border-slate-800 bg-slate-900 px-4 py-3" data-testid={`admin-package-${p.code}`}>
                    <div>
                      <p className="font-medium text-white">{p.name} · {p.credits} Credit</p>
                      <p className="text-xs text-slate-500">{p.is_free ? "Gratis" : formatRupiah(p.price)} · urutan {p.display_order}</p>
                      {!p.is_free && <div className="mt-2 flex items-center gap-2">
                        <label className="text-xs text-slate-400" htmlFor={`price-${p.code}`}>Harga (Rp)</label>
                        <input id={`price-${p.code}`} type="number" min="1" max="100000000" step="1"
                          value={priceEdits[p.code] ?? p.price}
                          onChange={(event) => setPriceEdits({ ...priceEdits, [p.code]: event.target.value })}
                          className="w-36 rounded border border-slate-700 bg-slate-950 px-2 py-1 text-sm text-white" />
                        <button type="button" onClick={() => savePackagePrice(p)} disabled={acting === `price:${p.code}`}
                          className="rounded bg-indigo-600 px-2 py-1 text-xs text-white disabled:opacity-50">Simpan harga</button>
                      </div>}
                    </div>
                    <button onClick={() => togglePackage(p.code, !p.is_active)} data-testid={`toggle-package-${p.code}`}
                      className={`rounded-full px-3 py-1 text-xs font-semibold ${p.is_active ? "bg-emerald-600 text-white" : "bg-slate-700 text-slate-300"}`}>
                      {p.is_active ? "Aktif" : "Nonaktif"}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {tab === "business" && (
          <div className="max-w-xl space-y-4" data-testid="admin-business-info">
            <h2 className="font-heading text-lg font-bold text-white">Informasi Bisnis</h2>
            <div className="rounded-xl border border-slate-800 bg-slate-900 p-6">
              <p className="font-heading text-lg font-semibold text-white">{BUSINESS.name}</p>
              <div className="mt-3 flex items-start gap-2 text-sm text-slate-400">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{BUSINESS.address.line1}, {BUSINESS.address.line2}, {BUSINESS.address.line3}</span>
              </div>
              <a href={waLink()} target="_blank" rel="noopener noreferrer"
                className="mt-3 inline-flex items-center gap-2 text-sm font-medium text-indigo-400 hover:underline">
                <MessageCircle className="h-4 w-4" /> WhatsApp Admin: {BUSINESS.whatsappDisplay}
              </a>
            </div>
          </div>
        )}

        {tab === "health" && health && (
          <div className="max-w-lg space-y-2" data-testid="admin-health">
            <HealthRow name="Backend" data={health.backend} />
            <HealthRow name="MongoDB" data={health.mongodb} />
            <HealthRow name="Object Storage" data={health.object_storage} />
            <HealthRow name="OpenAI" data={health.openai} />
            <HealthRow name="KIE" data={health.kie} />
            {health.heygen && <HealthRow name="HeyGen" data={health.heygen} />}
          </div>
        )}
      </main>
    </div>
  );
}
